import {ReactNode, useEffect, useState} from 'react';

import * as jose from 'jose';
import {JWTPayload} from 'jose';

type ProtectedComponentProps = {
  allowedRoles: string[];
  /** Oculta el contenido a quien tenga alguno, aunque esté en `allowedRoles`. */
  deniedRoles?: string[];
  debugKey?: string;
  children: ReactNode;
};

export function hasRoleAccess(
  roles: string[],
  allowedRoles: string[],
  deniedRoles?: string[],
) {
  return (
    allowedRoles.some(role => roles.includes(role)) &&
    !deniedRoles?.some(role => roles.includes(role))
  );
}

export function ProtectedComponent(props: ProtectedComponentProps) {
  const {allowedRoles, deniedRoles, children, debugKey} = props;
  const [roles, setRoles] = useState<string[]>([]);

  useEffect(() => {
    setRoles(getUserRoles());
  }, []);

  if (roles.length === 0) {
    if (debugKey) {
      console.debug(`[${debugKey}]`, '⏳ User roles not loaded yet');
    }
    return <></>;
  }

  if (hasRoleAccess(roles, allowedRoles, deniedRoles)) {
    if (debugKey) {
      console.debug(
        `[${debugKey}]`,
        '✅ User has one of the required roles',
        allowedRoles,
      );
    }
    return <>{children}</>;
  }
  if (debugKey) {
    console.debug(
      `[${debugKey}]`,
      '❌ User does not have any of the required roles',
      allowedRoles,
    );
  }
  return <></>;
}

type IToken = JWTPayload & {
  realm_access: {
    roles: string[];
  };
  resource_access: {
    [resourceName: string]: {
      roles: string[];
    };
  };
};

/**
 * Sin roles ante un token ilegible: se llama durante renders (incluido el
 * popup del calendario, sin ningún error boundary encima) y un throw aquí dejaría
 * la vista en blanco. Devuelve una copia; el array del token no se muta.
 */
export function getUserRoles() {
  const currentToken = localStorage.getItem('react-token');
  if (!currentToken) {
    return [];
  }
  try {
    const decodedJWT = jose.decodeJwt(currentToken) as IToken;
    const roles = [...(decodedJWT.realm_access?.roles ?? [])];
    Object.entries(decodedJWT.resource_access ?? {}).forEach(
      ([resourceName, resource]) => {
        const resourceRoles = (resource?.roles ?? []).map(
          role => `${resourceName}.${role}`,
        );
        roles.push(...resourceRoles);
      },
    );
    return roles;
  } catch {
    console.warn('[getUserRoles] token ilegible; se asume sin roles');
    return [];
  }
}
